// useReplayState.ts - Core replay state management hook
import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import type { UpdateEvent } from "../../../types/domain";

export type ReplaySpeed = 0.5 | 1 | 2 | 4;

export type ReplayState = {
  isReplayMode: boolean;
  isPlaying: boolean;
  currentTime: number; // Current playback time in seconds
  duration: number; // Total session duration in seconds
  speed: ReplaySpeed;
};

export type UseReplayStateReturn = {
  state: ReplayState;

  // Actions
  enterReplay: (events: UpdateEvent[]) => void;
  exitReplay: () => void;
  play: () => void;
  pause: () => void;
  seek: (time: number) => void;
  setSpeed: (speed: ReplaySpeed) => void;

  // Computed
  visibleEvents: UpdateEvent[];
  allEvents: UpdateEvent[];
};

export function useReplayState(): UseReplayStateReturn {
  const [isReplayMode, setIsReplayMode] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeedState] = useState<ReplaySpeed>(1);
  const [allEvents, setAllEvents] = useState<UpdateEvent[]>([]);

  const animationFrameRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number>(0);

  // Seconds per event when no timing data (for synthetic timing)
  const SECONDS_PER_EVENT = 3;

  // Calculate duration from events
  const calculateDuration = useCallback((events: UpdateEvent[]): number => {
    if (events.length === 0) return 0;

    let maxEndSec = 0;
    for (const e of events) {
      const endSec = (e as any).end_sec;
      if (typeof endSec === "number" && endSec > maxEndSec) {
        maxEndSec = endSec;
      }
    }

    // If no timing data, use synthetic timing (SECONDS_PER_EVENT per event)
    if (maxEndSec === 0) {
      return events.length * SECONDS_PER_EVENT;
    }

    return maxEndSec;
  }, []);

  // Animation loop for playback
  const tick = useCallback(() => {
    const now = performance.now();
    const delta = (now - lastFrameTimeRef.current) / 1000; // Convert to seconds
    lastFrameTimeRef.current = now;

    setCurrentTime((prev) => {
      const next = prev + delta * speed;
      if (next >= duration) {
        // Reached end - stop playback
        setIsPlaying(false);
        return duration;
      }
      return next;
    });

    animationFrameRef.current = requestAnimationFrame(tick);
  }, [speed, duration]);

  // Start/stop animation loop when playing state changes
  useEffect(() => {
    if (isPlaying && isReplayMode) {
      lastFrameTimeRef.current = performance.now();
      animationFrameRef.current = requestAnimationFrame(tick);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, isReplayMode, tick]);

  // Enter replay mode
  const enterReplay = useCallback(
    (events: UpdateEvent[]) => {
      // Debug: find all unique end_sec values
      const endSecValues = events
        .map((e) => (e as any).end_sec)
        .filter((v): v is number => typeof v === "number")
        .sort((a, b) => a - b);
      const uniqueEndSecs = Array.from(new Set(endSecValues));
      console.log("[useReplayState] Unique end_sec values (first 10, last 10):", {
        count: uniqueEndSecs.length,
        first10: uniqueEndSecs.slice(0, 10),
        last10: uniqueEndSecs.slice(-10),
        max: uniqueEndSecs[uniqueEndSecs.length - 1],
      });

      const dur = calculateDuration(events);
      console.log("[DBG enterReplay] count=", events.length, "dur=", dur, "sec =", (dur / 60).toFixed(1), "min", "synthetic?=", dur === events.length * 3);

      setAllEvents(events);
      setDuration(dur);
      setCurrentTime(0);
      setIsReplayMode(true);
      setIsPlaying(false);
      setSpeedState(1);
    },
    [calculateDuration]
  );

  // Exit replay mode
  const exitReplay = useCallback(() => {
    setIsReplayMode(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setAllEvents([]);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  // Play
  const play = useCallback(() => {
    if (!isReplayMode) return;
    // If at end, restart from beginning
    if (currentTime >= duration) {
      setCurrentTime(0);
    }
    setIsPlaying(true);
  }, [isReplayMode, currentTime, duration]);

  // Pause
  const pause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Seek to time
  const seek = useCallback(
    (time: number) => {
      const clampedTime = Math.max(0, Math.min(time, duration));
      setCurrentTime(clampedTime);
    },
    [duration]
  );

  // Set speed
  const setSpeed = useCallback((newSpeed: ReplaySpeed) => {
    setSpeedState(newSpeed);
  }, []);

  // Check if events have timing data
  const hasTimingData = useMemo(() => {
    return allEvents.some((e) => typeof (e as any).end_sec === "number");
  }, [allEvents]);

  // Filter visible events based on current time
  const visibleEvents = useMemo(() => {
    if (!isReplayMode) return allEvents;

    return allEvents.filter((e, index) => {
      if (hasTimingData) {
        // Use actual timing data
        const endSec = (e as any).end_sec;
        if (typeof endSec !== "number") {
          // Event without timing in a timed session - show if any prior event is visible
          return index === 0 ? currentTime > 0 : true;
        }
        return endSec <= currentTime;
      } else {
        // No timing data - use synthetic timing based on index
        // Each event appears after (index + 1) * SECONDS_PER_EVENT
        const syntheticTime = (index + 1) * SECONDS_PER_EVENT;
        return currentTime >= syntheticTime;
      }
    });
  }, [allEvents, currentTime, isReplayMode, hasTimingData]);

  const state: ReplayState = {
    isReplayMode,
    isPlaying,
    currentTime,
    duration,
    speed,
  };

  return {
    state,
    enterReplay,
    exitReplay,
    play,
    pause,
    seek,
    setSpeed,
    visibleEvents,
    allEvents,
  };
}
